import { Button, Divider, Form, Input, message, Modal, Select, Space, Switch, Typography } from "antd";
import { StarFilled, UserAddOutlined } from "@ant-design/icons";
import { useCallback, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import axiosInstance, { endpoints } from "services/axios";
import { BooleanReturnType } from "hooks/use-boolean";
import { IPartnerEmployee } from "models";
import PhoneInput from "./phone-input";
import PhotoUpload from "./photo-upload";

interface Props {
  modalBool: BooleanReturnType;
  refetch: any;
  companyList: { key: string; label: string; value: string }[];
}

const EmployeeDialog = ({ modalBool, refetch, companyList }: Props) => {
  const [form] = Form.useForm();
  const primary = Form.useWatch("primary", form);

  const editing: (IPartnerEmployee & { companyId?: string }) | undefined = modalBool.data?.employee;
  const presetCompanyId: string | undefined = modalBool.data?.companyId;

  useEffect(() => {
    if (!modalBool.value) return;
    form.setFieldsValue({
      companyId: editing?.companyId ?? presetCompanyId,
      firstName: editing?.firstName,
      lastName: editing?.lastName,
      phoneNumber: editing?.phoneNumber,
      photoId: editing?.photoId ?? undefined,
      primary: !!editing?.primary,
    });
  }, [modalBool.value, editing, presetCompanyId, form]);

  const { mutate: saveEmployee, isPending } = useMutation({
    mutationFn: async (data: any) =>
      editing
        ? await axiosInstance.put(endpoints.partner.employee, { id: editing.id, ...data })
        : await axiosInstance.post(endpoints.partner.employee, data),
    onSuccess: () => {
      message.success("Saqlandi!");
      cancel();
    },
  });

  const onFinish = useCallback(
    (data: any) => saveEmployee({ ...data, phoneNumber: data.phoneNumber?.replace(/\s/g, "") }),
    [saveEmployee]
  );

  const cancel = () => {
    modalBool.onFalse();
    form.resetFields();
    refetch();
  };

  return (
    <Modal
      open={modalBool.value}
      onCancel={cancel}
      title={
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-base shrink-0">
            <UserAddOutlined />
          </span>
          <Typography.Title className="m-0" level={5}>
            {editing ? "Xodimni tahrirlash" : "Xodim qo'shish"}
          </Typography.Title>
        </div>
      }
      footer={null}
      destroyOnClose
      width={620}
    >
      <Form onFinish={onFinish} form={form} layout="vertical" className="mt-3">
        <div className="flex gap-6 items-start">
          <div className="flex-1 min-w-0">
            <Form.Item name="companyId" label="Kompaniya" rules={[{ required: true, message: "Kompaniyani tanlang!" }]}>
              <Select options={companyList} placeholder="Kompaniyani tanlang" />
            </Form.Item>

            <Space.Compact block>
              <Form.Item className="flex-1" name="firstName" label="Ism" rules={[{ required: true, message: "Ism kiriting!" }]}>
                <Input placeholder="Ism" />
              </Form.Item>
              <Form.Item className="flex-1 ml-2" name="lastName" label="Familya" rules={[{ required: true, message: "Familya kiriting!" }]}>
                <Input placeholder="Familya" />
              </Form.Item>
            </Space.Compact>

            <Form.Item
              name="phoneNumber"
              label="Telefon raqami"
              rules={[
                { required: true, message: "Telefon raqamini kiriting!" },
                { pattern: /^\+998 \d{2} \d{3} \d{2} \d{2}$/, message: "Telefon raqamini to'liq kiriting!" },
              ]}
            >
              <PhoneInput />
            </Form.Item>
          </div>

          <div className="flex flex-col items-center gap-2 shrink-0">
            <span className="text-sm font-medium">
              <span style={{ color: "#ff4d4f" }} className="mr-1">
                *
              </span>
              Rasm yuklash
            </span>
            <Form.Item
              name="photoId"
              valuePropName="photoId"
              rules={[{ required: true, message: "Rasm yuklang!" }]}
              className="mb-0"
            >
              <PhotoUpload />
            </Form.Item>
          </div>
        </div>

        <Divider className="my-4" />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span>Asosiy xodim sifatida belgilash</span>
            <StarFilled className="text-yellow-400 cursor-pointer" onClick={() => form.setFieldValue("primary", !primary)} />
          </div>
          <Form.Item name="primary" valuePropName="checked" noStyle>
            <Switch />
          </Form.Item>
        </div>
        <Typography.Text type="secondary" className="text-xs block mt-1">
          Yoqilsa, ushbu xodim kompaniyaning asosiy xodimi sifatida ro'yxatda belgilanadi.
        </Typography.Text>

        <Form.Item className="flex justify-end mb-0 mt-5">
          <Space>
            <Button onClick={cancel}>Bekor qilish</Button>
            <Button loading={isPending} type="primary" htmlType="submit">
              Saqlash
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EmployeeDialog;
